import React from 'react';
import { X, Copy, Send } from 'lucide-react';
import { BOZ_MESSAGES, BozMessageKey } from '../../constants/bozMessages';
import toast from 'react-hot-toast';

interface QuickActionPopupProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectMessage: (message: string) => void;
  searchQuery?: string;
  isAttached?: boolean;
}

export const QuickActionPopup: React.FC<QuickActionPopupProps> = ({
  isOpen,
  onClose,
  onSelectMessage,
  searchQuery = '',
  isAttached = false
}) => {
  if (!isOpen) return null;

  const handleCopyMessage = (message: string) => {
    navigator.clipboard.writeText(message);
    toast.success('Message copied to clipboard!');
  };

  const handleSelectMessage = (message: string) => {
    onSelectMessage(message);
    onClose();
  };

  const messageEntries = Object.entries(BOZ_MESSAGES) as [BozMessageKey, string][];
  
  // Filter messages based on search query
  const filteredMessages = searchQuery
    ? messageEntries.filter(([key]) => {
        // Check if key starts with search query (case-insensitive)
        const keyNormalized = key.replace(/_/g, ' ').toLowerCase();
        const searchNormalized = searchQuery.toLowerCase();
        return keyNormalized.startsWith(searchNormalized);
      })
    : messageEntries;

  // If attached mode, render as dropdown
  if (isAttached) {
    return (
      <div className="bg-white rounded-lg shadow-lg border border-gray-200 w-full max-h-[400px] overflow-hidden">
        {/* Header - Compact */}
        <div className="flex items-center justify-between px-3 py-2 border-b border-gray-200 bg-gray-50">
          <h3 className="text-sm font-semibold text-gray-700">Quick Actions</h3>
          <button
            onClick={onClose}
            className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-200 rounded transition-colors"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Content - Scrollable */}
        <div className="overflow-y-auto max-h-[350px]">
          {filteredMessages.length === 0 ? (
            <div className="text-center py-6 px-4">
              <p className="text-sm text-gray-500">No quick actions found matching "{searchQuery}"</p>
            </div>
          ) : (
            <div className="py-1">
              {filteredMessages.map(([key, message]) => (
                <button
                  key={key}
                  onClick={() => handleSelectMessage(message)}
                  className="w-full text-left px-3 py-2 hover:bg-gray-50 transition-colors border-b border-gray-100 last:border-b-0"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <h4 className="font-medium text-gray-900 text-sm mb-1">
                        {key.replace(/_/g, ' ')}
                      </h4>
                      <p className="text-xs text-gray-600 line-clamp-2">
                        {message.substring(0, 100)}{message.length > 100 ? '...' : ''}
                      </p>
                    </div>
                    <div className="flex items-center space-x-1 ml-2 flex-shrink-0">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleCopyMessage(message);
                        }}
                        className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-200 rounded transition-colors"
                        aria-label="Copy message"
                        title="Copy to clipboard"
                      >
                        <Copy className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  // Original modal mode
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Quick Actions</h3>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="overflow-y-auto max-h-[60vh] p-4">
          {filteredMessages.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-500">No quick actions found matching "{searchQuery}"</p>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredMessages.map(([key, message]) => (
              <div
                key={key}
                className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-start justify-between mb-2">
                  <h4 className="font-medium text-gray-900 text-sm">
                    {key.replace(/_/g, ' ')}
                  </h4>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => handleCopyMessage(message)}
                      className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
                      aria-label="Copy message"
                      title="Copy to clipboard"
                    >
                      <Copy className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleSelectMessage(message)}
                      className="p-1 text-primary-600 hover:text-primary-700 hover:bg-primary-50 rounded transition-colors"
                      aria-label="Send message"
                      title="Send this message"
                    >
                      <Send className="h-4 w-4" />
                    </button>
                  </div>
                </div>
                <div className="text-sm text-gray-700 whitespace-pre-line">
                  {message}
                </div>
              </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end p-4 border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};
